import { FC, FormEvent } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import { Input, Label } from '@/components/Forms';
import FileField from '@/components/Forms/FileField';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { handleFormErrors } from '@/utils/helpers';
import { Dispatch, RootState } from '@/utils/store';

const Card = styled.div`
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  padding: 24px 24px 40px;
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 32px;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Headline = styled(Text)`
  margin: 0 0 24px;
`;

type FormData = {
  name: string;
  icon: File | null;
};

const Profile: FC = () => {
  const { teamId } = useParams();

  const dispatch = useDispatch<Dispatch>();

  const team = useSelector((rootState: RootState) => rootState.teams[teamId!]);

  const alerts = useAlerts();

  const methods = useForm<FormData>({
    defaultValues: {
      name: team!.name,
      icon: null,
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async ({ icon, name }) => {
      try {
        const formData = new FormData();

        formData.append('_method', 'PUT');

        formData.append('name', name);

        if (icon) {
          formData.append('icon', icon);
        }

        await dispatch.teams.updateTeam({ teamId: teamId!, formData });

        alerts.success(`Team was successfully updated`);
      } catch (error) {
        handleFormErrors<FormData>(error, setError);
      }
    })(e);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} autoComplete="off">
        <Card>
          <Headline $type="caption" as="h2">
            Profile settings
          </Headline>
          <FormGroup>
            <Label htmlFor="nameInput">Team Name</Label>
            <Input
              {...register('name')}
              $hasError={!!errors.name}
              id="nameInput"
              placeholder="Enter Team Name"
            />
            {!!errors.name && (
              <ValidationMessage>{errors.name.message}</ValidationMessage>
            )}
          </FormGroup>
          <div>
            <FileField
              fileType="image"
              name="icon"
              label="Profile Photo"
              existingMedia={team.icon}
            />
          </div>
        </Card>
        <Footer>
          <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
            Save
          </Button>
        </Footer>
      </form>
    </FormProvider>
  );
};

export default Profile;
